<template>
  <SliderRoot
    :modelValue="value"
    @update:modelValue="update"
    class="WSlider SliderRoot"
    :min="min"
    :max="max"
    :step="step"
    :disabled="disabled"
  >
    <SliderTrack class="SliderTrack">
      <SliderRange class="SliderRange" />
    </SliderTrack>
    <SliderThumb v-for="i in thumbsLength" :key="i" class="SliderThumb" aria-label="Volume" />
  </SliderRoot>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'radix-vue';

  type IValue = number | number[];

  export default {
    name: 'WSlider',
    components: { SliderRange, SliderRoot, SliderThumb, SliderTrack },
    emits: ['update:modelValue', 'change'],
    props: {
      modelValue: {
        type: [Number, Array] as PropType<IValue>,
        default: 0,
      },
      min: { type: Number, default: 0 },
      max: { type: Number, default: 10 },
      step: { type: Number, default: 1 },
      disabled: Boolean,
      range: Boolean,
      length: { type: Number, default: 1 },
    },

    methods: {
      update(value: number[]) {
        const newValue = this.isRange ? value : value[0];
        this.$emit('update:modelValue', newValue);
        this.$emit('change', newValue);
      },
    },

    computed: {
      isRange() {
        return this.thumbsLength > 1;
      },
      thumbsLength() {
        return Math.max(this.length, this.range ? 2 : 1);
      },
      value() {
        return this.isRange ? this.modelValue : [this.modelValue];
      },
    },
  };
</script>

<style lang="scss">
  @import '@/assets/styles/variables';

  .WSlider {
    $focussed-color: $primary-main;
    $backdrop-color: $bg-dark;
    $slider-thumb-size: 0.75rem;

    &.SliderRoot {
      position: relative;
      display: flex;
      align-items: center;
      user-select: none;
      touch-action: none;
      width: 100%;
      height: 1.25rem;
    }

    .SliderTrack {
      background-color: $backdrop-color;
      position: relative;
      flex-grow: 1;
      border-radius: 1rem;
      height: 0.375rem;
    }

    .SliderRange {
      position: absolute;
      background-color: $focussed-color;
      box-sizing: border-box;
      border: 1px solid $white;
      border-radius: 9999px;
      height: 100%;
    }

    .SliderThumb {
      display: block;
      width: $slider-thumb-size;
      height: $slider-thumb-size;
      background-color: $focussed-color;
      box-shadow: 0 2px 10px $shadow-color;
      border-radius: 100%;
      outline: 2px solid $white;
      transition: outline linear 0.1s;
    }

    .SliderThumb:hover {
      outline: 4px solid $white;
      cursor: pointer;
    }
  }
</style>
